<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="8"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableClusters.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Area"
          label-for="filterArea"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableClusters.selected.area"
            placeholder="filter area here"
            input-id="filterArea"
            class="vs-white"
            label="area_name"
            clearable
            append-to-body
            :options="(tableClusters.options.areas)"
            :loading="(tableClusters.fetching.areas)"
            :disabled="(tableClusters.busy || tableClusters.fetching.areas)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterAreas"
          >
            <template #option="{ area_name }">
              <div
                class="text-break text-wrap"
                v-text="area_name"
              />
            </template>
            <template #selected-option="{ area_name }">
              <div
                class="w-225px w-md-100px w-lg-200px text-truncate"
                v-text="area_name"
              />
            </template>
            <template #no-options="">
              no available area
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createCluster"
        >
          Create Cluster
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableClusters"
      :provider="tableProvider"
      :fields="tableClusters.fields"
      :filter="tableClusters.filter"
      :filter-on="tableClusters.filterOn"
      :sort-by="tableClusters.sortBy"
      :sort-desc="tableClusters.sortDesc"
      :sort-direction="tableClusters.sortDirection"
      :total-rows="tableClusters.totalRows"
      :current-page="tableClusters.currentPage"
      :per-page="tableClusters.perPage"
      :stacked="tableClusters.stacked"
      :busy="tableClusters.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableClusters.busy"
            @click.prevent="editCluster(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalCluster"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MClusterTitle"
    >
      <ValidationObserver
        ref="formCluster"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="cluster code"
                vid="cluster_code"
                rules="max:150"
              >
                <BFormGroup
                  label="Cluster Code"
                  label-for="fClusterCode"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fClusterCode"
                    v-model="formCluster.cluster_code"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter cluster code here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="cluster name"
                vid="cluster_name"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Cluster Name"
                  label-for="fClusterName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fClusterName"
                    v-model="formCluster.cluster_name"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter cluster name here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="area"
                name="area"
                rules="required"
              >
                <BFormGroup
                  label="Area"
                  label-for="fArea"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.area"
                    input-id="fArea"
                    label="area_name"
                    append-to-body
                    placeholder="search area here"
                    :options="options.areas"
                    :disabled="fetching.areas"
                    :loading="fetching.areas"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchAreas"
                  >
                    <template #no-options="">
                      no available area
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formCluster.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{cancel}">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateCluster"
        >
          {{ state.editing ? 'Update Cluster' : 'Create Cluster' }}
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MAreas from '@/mixins/locations/areas'

import { SAdminCluster } from '@/services/admin'

export default {
  name: 'VAdminClusters',

  mixins: [
    MxMisc,
    MxFormatter,
    MAreas
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Clusters'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formCluster: {
        id: '',
        area_id: '',
        cluster_code: '',
        cluster_name: '',
        active: 1
      },
      tableClusters: {
        busy: false,
        fetching: {
          areas: false
        },
        selected: {
          area: ''
        },
        options: {
          areas: []
        },
        filter: {
          search: '',
          area: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 6, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'cluster_code', class: 'min-w-150px' },
          { mobile: 2, key: 'cluster_name', class: 'min-w-175px' },
          { mobile: 3, key: 'area.area_name', class: 'min-w-150px' },
          { mobile: 4, key: 'active', class: 'text-md-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 5, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MClusterTitle () {
      return this.state.editing ? 'Edit Cluster' : 'Create Cluster'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'selected.area' (area) {
      this.formCluster.area_id = area?.id || ''
    },
    'tableClusters.selected.area' (area) {
      this.tableClusters.filter.area = area?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getAreas(this.tableClusters)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableClusters.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      }

      return await SAdminCluster.get(payload).then(
        ({ data }) => {
          this.tableClusters.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableClusters.totalRows = 0
        return []
      }).finally(() => {
        this.tableClusters.busy = false
      })
    },

    async searchFilterAreas (search, loading) {
      this.searchAreasProvider(this.tableClusters, search, loading)
    },

    createCluster () {
      this.state.editing = false

      this.formCluster = Object.assign(this.formCluster, {
        id: '',
        area_id: '',
        cluster_code: '',
        cluster_name: '',
        active: 1
      })

      this.getAreas(this)

      this.$refs.modalCluster.show()
    },

    editCluster (cluster) {
      this.state.editing = true

      this.formCluster = Object.assign(this.formCluster, {
        id: cluster.id,
        cluster_code: cluster.cluster_code,
        cluster_name: cluster.cluster_name,
        active: cluster.active
      })

      this.getAreas(this).finally(() => {
        this.selected.area = QUERY.find(this.options.areas, {
          id: cluster.area_id
        }) || ''
      })

      this.$refs.modalCluster.show()
    },

    async validateCluster () {
      await this.$refs.formCluster.validate().then(
        async validCluster => {
          if (!validCluster) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Cluster?</h6>' : '<h6>Create this Cluster?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putCluster() : this.postCluster()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formCluster.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postCluster () {
      return await SAdminCluster.post(this.formCluster).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalCluster.hide()
            this.$refs.tableClusters.refetch()
          })
        }
      )
    },

    async putCluster () {
      return await SAdminCluster.put(this.formCluster).then(
        ({ data: { message, cluster } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalCluster.hide()
            this.$refs.tableClusters.updateRow({
              id: cluster.id,
              area: cluster.area,
              area_id: cluster.area_id,
              cluster_code: cluster.cluster_code,
              cluster_name: cluster.cluster_name,
              active: cluster.active,
              updated_at: cluster.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
